import { sanitizeClipboardHtml, inlineHtmlToMarkdown } from './sanitizeHtml';

/**
 * range.cloneContents() drops <b>/<i>/<code>/variable pills when the
 * selection is only the text node inside them — re-wrap so copy/paste keeps
 * the formatting / token identity.
 */
const wrapFragmentWithAncestorFormats = (range, fragmentHtml) => {
  let html = fragmentHtml || '';
  if (!html) return html;
  const startEl =
    range.startContainer.nodeType === Node.ELEMENT_NODE
      ? range.startContainer
      : range.startContainer.parentElement;
  const endEl =
    range.endContainer.nodeType === Node.ELEMENT_NODE
      ? range.endContainer
      : range.endContainer.parentElement;
  const same = (selector) => {
    const a = startEl?.closest?.(selector);
    return a && a === endEl?.closest?.(selector) ? a : null;
  };
  // Variable pill: cloneContents often yields bare display text (the resolved
  // value). Re-emit the live pill so paste keeps data-var-name / state.
  const pill = same('[data-doc-variable], [data-doc-math]');
  if (pill && !/data-doc-variable|data-doc-math/i.test(html)) {
    return pill.outerHTML;
  }
  // Innermost first so <b><code>x</code></b> survives a full select
  if (same('code') && !/^\s*<code[\s>]/i.test(html)) html = `<code>${html}</code>`;
  if (same('b, strong') && !/^\s*<(b|strong)[\s>]/i.test(html)) html = `<b>${html}</b>`;
  if (same('i, em') && !/^\s*<(i|em)[\s>]/i.test(html)) html = `<i>${html}</i>`;
  return html;
};

/**
 * Write a contenteditable text selection to the clipboard with real <code>
 * tags (and markdown plain text). Native browser copy often flattens our
 * <code> elements into styled spans, which then get stripped on paste.
 * Selections inside a quote callout are exported as markdown "> " / <blockquote>
 * so paste recreates the quote (paste already understands both forms).
 */
export const writeSelectionToClipboardData = (clipboardData) => {
  if (!clipboardData) return false;
  const selection = window.getSelection();
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) return false;

  const range = selection.getRangeAt(0);
  const anchorEl =
    range.commonAncestorContainer.nodeType === Node.ELEMENT_NODE
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentElement;
  const editable = anchorEl?.closest?.('.doc-editable');
  if (!editable) return false;

  const holder = document.createElement('div');
  holder.appendChild(range.cloneContents());
  const fragmentHtml = wrapFragmentWithAncestorFormats(range, holder.innerHTML);
  const sanitized = sanitizeClipboardHtml(fragmentHtml);
  const plain = inlineHtmlToMarkdown(sanitized || fragmentHtml).replace(
    /<br\s*\/?>/gi,
    '\n'
  );

  // Empty/unresolved pills are stripped from copy. If the selection was only
  // those, still claim the event so native copy doesn't paste the bare name.
  if (!sanitized && !plain) {
    const onlyEmptyVars =
      /data-doc-variable|data-doc-math/i.test(fragmentHtml) ||
      !!holder.querySelector?.('[data-doc-variable], [data-doc-math]');
    if (!onlyEmptyVars) return false;
    clipboardData.setData('text/html', '');
    clipboardData.setData('text/plain', '');
    return true;
  }

  const inQuote = Boolean(editable.closest('.callout-variant-quote'));
  if (inQuote) {
    const quotedPlain = plain
      .split('\n')
      .map((line) => `> ${line}`)
      .join('\n');
    clipboardData.setData('text/html', `<blockquote>${sanitized}</blockquote>`);
    clipboardData.setData('text/plain', quotedPlain);
    return true;
  }

  clipboardData.setData('text/html', sanitized);
  clipboardData.setData('text/plain', plain);
  return true;
};
